import { useAtom } from 'jotai';
import React, { FC, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation, Trans } from 'react-i18next';
import styled from 'styled-components';

import { reservationConfirmCharacterPng } from '../assets/images';
import { P2L_EMBEDDED_VIDEO } from '../constants';
import { Language } from '../enum';
import { notificationAtom } from '../jotai-atoms/notification.atom';
import { reservationDataAtom } from '../jotai-atoms/reservation-data.atom';
import { userAtom } from '../jotai-atoms/user.atom';
import { LiveClassBookingService } from '../services/liveclass-booking';
import { NotificationService } from '../services/notification';
import { Modal, ModalProps } from './Modal';

export interface ModalReservationConfirmProps {
  exampleBg?: boolean;
  isGameClass: boolean;
  show: boolean;
  onClose: () => void;
  liveClassId?: number;
}

export const ModalReservationConfirm: FC<ModalReservationConfirmProps> = ({
  show,
  onClose,
  exampleBg,
  liveClassId,
  isGameClass,
}) => {
  const __ = useTranslation().t;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reservationData, setReservationData] = useAtom(reservationDataAtom);
  const [user] = useAtom(userAtom);
  const [notification, setNotification] = useAtom(notificationAtom);

  const lang = (user?.lang || 'en') as Language;
  const p2lVideoUrl = P2L_EMBEDDED_VIDEO[lang];
  const userName = user?.firstName;
  const isP2LEmbeddedEnabled = isMobile
    ? user?.context?.p2lEmbeddedZoomPlayerMobileEnabled
    : user?.context?.p2lEmbeddedZoomPlayerEnabled;

  const modalProps: ModalProps = {
    show,
    onClose,
    type: 'default',
    exampleBg,
    title: __('confirmAttendance'),
    submitButtonText: __('yesIWillAttend'),
    cancelButtonText: __('cancelClass'),
    hideCloseButton: true,
  };

  const onSubmit = () => {
    if (!liveClassId) {
      onClose();
      alert(__('whopsSomethingWentWrong'));
      return;
    }
    setIsLoading(true);
    LiveClassBookingService.postConfirmPresence({
      liveclass_id: liveClassId,
    })
      .then(() => {
        setIsLoading(false);
        if (notification) {
          NotificationService.putNotificationRead(notification.data.notificationId);
          setNotification(null);
        }
        onClose();
      })
      .catch(() => {
        setIsLoading(false);
        alert(__('whopsSomethingWentWrong'));
      });
  };

  const onCancel = () => {
    if (!liveClassId) {
      onClose();
      alert(__('whopsSomethingWentWrong'));
      return;
    }
    setIsLoading(true);
    LiveClassBookingService.postCancel({
      liveclass_id: liveClassId,
    })
      .then(() => {
        setIsLoading(false);
        setReservationData({
          ...reservationData,
          forceRefreshUserData: !reservationData?.forceRefreshUserData,
        });

        if (notification) {
          NotificationService.putNotificationRead(notification.data.notificationId);
          setNotification(null);
        }

        onClose();
      })
      .catch(() => {
        setIsLoading(false);
        alert(__('whopsSomethingWentWrong'));
      });
  };

  return (
    <StyledModal
      {...modalProps}
      onClose={onCancel}
      onSubmit={onSubmit}
      submitButtonDisabled={isLoading}
      cancelButtonDisabled={isLoading}
    >
      <ImageContainer>
        <img src={reservationConfirmCharacterPng} alt="Character" />
      </ImageContainer>
      <StyledH4>{__('heyUserReady', { 1: userName })}</StyledH4>
      <StyledP>
        {isGameClass && isP2LEmbeddedEnabled ? (
          <Trans alt={__('classAboutToStartWatchVideo')} i18nKey="classAboutToStartWatchVideo">
            The class is about to start! Watch this video to find out what's new in{' '}
            <StyledA href={p2lVideoUrl} target="_blank">
              Play2Learn
            </StyledA>
            !
          </Trans>
        ) : (
          <Trans
            alt={__('classAboutToStartZoomAppInstalledEnterHere')}
            i18nKey="classAboutToStartZoomAppInstalledEnterHere"
          >
            The class is about to start! Don't forget to have the Zoom app installed. If you don't
            have it yet, enter{' '}
            <StyledA href="https://zoom.us/support/download" target="_blank">
              here
            </StyledA>
            .
          </Trans>
        )}
      </StyledP>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  max-width: 565px;
`;

const StyledH4 = styled.h4`
  font-size: 20px;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.dark};
  text-align: center;
  font-family: 'Inter';
  font-weight: 700;
  margin-bottom: 12px;
`;

const StyledP = styled.p`
  text-align: center;
  font-family: 'Inter';
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
`;

const StyledA = styled.a`
  color: ${({ theme }) => theme.colors.dark};
  font-weight: bold;
`;
